import { useState, useEffect } from 'react';
import { DeviationFile } from '@wix/da-shared-react/pkg/types/deviation';

interface State {
  loaded: boolean;
  imageLoaderElement?: HTMLImageElement;
  image?: DeviationFile;
}

export default function useImageLoader(image?: DeviationFile) {
  const isSSR = typeof Image === `undefined`;
  const [state, setState] = useState<State>({
    image,
    imageLoaderElement: isSSR ? undefined : new Image(),
    loaded: false,
  });

  // When component is unmounted, clear onload callback to avoid a late `setState` call
  useEffect(
    () => () => {
      if (state.imageLoaderElement) {
        state.imageLoaderElement.onload = null;
      }
    },
    [state]
  );

  // Bypass the loader when rendering server-side or if no image was provided
  if (isSSR || !image) {
    return true;
  }

  // If no loader was setup yet, or if we want to load a new image, configure
  // `src` and `onload` so that we trigger a state change when the image loads
  const { imageLoaderElement, loaded } = state;
  if (!imageLoaderElement!.src || image !== state.image) {
    imageLoaderElement!.onload = () =>
      setState({ image, imageLoaderElement, loaded: true });
    imageLoaderElement!.src = image.src;
  }

  return Boolean(loaded);
}
